import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import * as HoldersActions from './holders.actions';
import { HoldersService } from '../../services/holders.service';
import { selectSelectedStock } from '../stocks/stocks.selectors';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root',
})
export class HoldersEffects {
  loadHolders$: any = {};
  loadHolder$: any = {};
  createHolder$: any = {};
  updateHolder$: any = {};
  deleteHolder$: any = {};
  holderSuccess$: any = {};

  constructor(
    private actions$: Actions,
    private holdersService: HoldersService,
    private router: Router,
    private store: Store
  ) {
    this.loadHolders$ = createEffect(() =>
      this.actions$.pipe(
        ofType(HoldersActions.loadHolders),
        switchMap(({ stockId, filters }) =>
          this.holdersService.getHolders(stockId, filters?.pageNumber, filters?.pageSize).pipe(
            map((response: any) => {
              const holders = response.data.items || response;
              const pagination = {
                currentPage: response.data.pageNumber,
                pageSize: response.data.pageSize,
                totalItems: response.data.totalCount,
              };
              return HoldersActions.loadHoldersSuccess({ holders, pagination });
            }),
            catchError((error) => of(HoldersActions.loadHoldersFailure({ error: error.message })))
          )
        )
      )
    );

    this.loadHolder$ = createEffect(() =>
      this.actions$.pipe(
        ofType(HoldersActions.loadHolder),
        switchMap(({ id }) =>
          this.holdersService.getHolderById(id).pipe(
            map((response: any) => {
              const holder = response.data || response;
              return HoldersActions.loadHolderSuccess({ holder });
            }),
            catchError((error) => of(HoldersActions.loadHolderFailure({ error: error.message })))
          )
        )
      )
    );

    this.createHolder$ = createEffect(() =>
      this.actions$.pipe(
        ofType(HoldersActions.createHolder),
        switchMap(({ holder }) =>
          this.holdersService.createHolder(holder).pipe(
            map((response: any) => {
              const createdHolder = response.data || response;
              return HoldersActions.createHolderSuccess({ holder: createdHolder });
            }),
            catchError((error) => of(HoldersActions.createHolderFailure({ error: error.message })))
          )
        )
      )
    );

    this.updateHolder$ = createEffect(() =>
      this.actions$.pipe(
        ofType(HoldersActions.updateHolder),
        switchMap(({ id, holder }) =>
          this.holdersService.updateHolder(id, holder).pipe(
            map((response: any) => {
              const updatedHolder = response.data || response;
              return HoldersActions.updateHolderSuccess({ holder: updatedHolder });
            }),
            catchError((error) => of(HoldersActions.updateHolderFailure({ error: error.message })))
          )
        )
      )
    );

    this.deleteHolder$ = createEffect(() =>
      this.actions$.pipe(
        ofType(HoldersActions.deleteHolder),
        switchMap(({ id }) =>
          this.holdersService.deleteHolder(id).pipe(
            map(() => HoldersActions.deleteHolderSuccess({ id })),
            catchError((error) => of(HoldersActions.deleteHolderFailure({ error: error.message })))
          )
        )
      )
    );

    // Navigate after success
    this.holderSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(
            HoldersActions.createHolderSuccess,
            HoldersActions.updateHolderSuccess,
            HoldersActions.deleteHolderSuccess
          ),
          tap(() => {
            this.store.select(selectSelectedStock).subscribe((stock) => {
              this.router.navigate(['/stocks', 'view', stock?.id, 'holder']);
            });
          })
        ),
      { dispatch: false }
    );
  }
}
